import { useRef, useState } from 'react'
import { Award, BadgeCheck, CalendarClock, FileBadge, Upload } from 'lucide-react'
import { Button, Divider, Field, Metric } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { UAS_CLASSES, UAS_CLASS_LIST } from '../../lib/classes'
import type { PilotProfile, UasClass } from '../../types'

/* ============================================================================
 * ПРОФИЛЬ ПИЛОТА
 *
 * Аналог учёта операторов по Part 107:
 *   • загрузка сертификата пилота;
 *   • привязка актива (БВС) по СЕРИЙНОМУ НОМЕРУ;
 *   • допустимый класс и высота;
 *   • срок действия медицинской справки и обучения.
 * ========================================================================= */

export const DEFAULT_PROFILE: PilotProfile = {
  name: '',
  certificateNo: '',
  certificateFileName: '',
  medicalValidUntil: '',
  assetSerial: '',
  uasClass: 'Н',
  trainingExpires: '',
}

export function PilotProfilePanel({
  profile,
  onChange,
}: {
  profile: PilotProfile
  onChange: (p: PilotProfile) => void
}) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [certError, setCertError] = useState<string | null>(null)

  const set = <K extends keyof PilotProfile>(k: K, v: PilotProfile[K]): void =>
    onChange({ ...profile, [k]: v })

  const classProfile = UAS_CLASSES[profile.uasClass ?? 'Н']
  const hasCert = Boolean(profile.certificateFileName)
  const hasAsset = Boolean(profile.assetSerial)
  const ready = hasCert && hasAsset && profile.name.trim() !== ''

  /** Проверка «пробелов» перед допуском к полётам */
  const gaps: Array<{ state: 'critical-error' | 'warning' | 'approved'; text: string }> = []
  if (!hasCert) {
    gaps.push({ state: 'critical-error', text: 'Не загружен сертификат пилота.' })
  }
  if (!hasAsset) {
    gaps.push({ state: 'critical-error', text: 'Не привязан актив по серийному номеру.' })
  }
  if (!profile.medicalValidUntil) {
    gaps.push({ state: 'warning', text: 'Не указан срок действия медицинской справки.' })
  }
  if (!profile.trainingExpires) {
    gaps.push({ state: 'warning', text: 'Не указан срок действия обучения по категориям.' })
  }

  return (
    <div className="space-y-2 p-2.5">
      {/* Сводка допуска */}
      <div
        className={`rounded-md border px-2.5 py-2 ut-stated ${
          ready ? 'ut-state-approved' : 'ut-state-warning'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <BadgeCheck size={12} aria-hidden />
            Допуск к полётам
          </div>
          <StateBadge state={ready ? 'approved' : 'critical-error'}>
            {ready ? 'ДОПУЩЕН' : 'ОГРАНИЧЕН'}
          </StateBadge>
        </div>
        <div className="mt-1 ut-metric text-[10px] text-ink-muted">
          Класс «{profile.uasClass ?? 'Н'}» · {classProfile.maxAltM} м · {classProfile.los} ·{' '}
          {classProfile.order === 'permission' ? 'разрешительный' : 'уведомительный'} порядок
        </div>
      </div>

      <Field label="ФИО пилота">
        <input
          className="ut-input"
          placeholder="Иванов Иван Иванович"
          value={profile.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Номер сертификата">
          <input
            className="ut-input ut-metric"
            placeholder="ПИЛ-2024-00001"
            value={profile.certificateNo ?? ''}
            onChange={(e) => set('certificateNo', e.target.value)}
          />
        </Field>
        <Field label="Класс БВС по сертификату">
          <select
            className="ut-select"
            value={profile.uasClass ?? 'Н'}
            onChange={(e) => set('uasClass', e.target.value as UasClass)}
          >
            {UAS_CLASS_LIST.map((c) => (
              <option key={c} value={c}>
                {c} — {UAS_CLASSES[c].label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Divider label="Сертификат" />

      {/* Загрузка файла сертификата */}
      <div>
        <span className="ut-label">Файл сертификата</span>
        <div className="flex items-center gap-2">
          <label className="ut-btn flex-1 cursor-pointer">
            <Upload size={12} aria-hidden />
            {profile.certificateFileName || 'Загрузить файл'}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (!f) return
                if (f.size > 5 * 1024 * 1024) {
                  setCertError('Файл больше 5 МБ')
                  return
                }
                setCertError(null)
                set('certificateFileName', f.name)
              }}
            />
          </label>
          {profile.certificateFileName && (
            <Button size="sm" onClick={() => set('certificateFileName', '')}>
              Убрать
            </Button>
          )}
        </div>
        {certError && <span className="mt-0.5 block text-[10px] text-critical">{certError}</span>}
        {profile.certificateFileName && !certError && (
          <div className="ut-stated ut-state-approved mt-1 flex items-center gap-1.5 rounded border px-1.5 py-1 text-[10px]">
            <FileBadge size={11} aria-hidden />
            Загружен: {profile.certificateFileName}
          </div>
        )}
      </div>

      <Divider label="Привязка актива" />

      <Field
        label="Серийный номер БВС"
        hint="Привязка актива выполняется по серийному номеру, как в учёте Part 107"
      >
        <input
          className="ut-input ut-metric"
          placeholder="GEOS-MINI-4471"
          value={profile.assetSerial ?? ''}
          onChange={(e) => set('assetSerial', e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Медсправка до">
          <input
            type="date"
            className="ut-input ut-metric"
            value={profile.medicalValidUntil ?? ''}
            onChange={(e) => set('medicalValidUntil', e.target.value)}
          />
        </Field>
        <Field label="Обучение до">
          <input
            type="date"
            className="ut-input ut-metric"
            value={profile.trainingExpires ?? ''}
            onChange={(e) => set('trainingExpires', e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Предел высоты"
          value={classProfile.maxAltM}
          unit="м"
          icon={Award}
          state="selected"
        />
        <Metric
          label="Предел массы"
          value={classProfile.maxMassKg}
          unit="кг"
          icon={CalendarClock}
        />
      </div>

      <Divider label="Пробелы допуска" />

      {gaps.length === 0 ? (
        <div className="ut-stated ut-state-approved rounded-md border px-2 py-1.5 text-[11px]">
          Документы в порядке — пилот допущен к полётам.
        </div>
      ) : (
        <div className="space-y-1">
          {gaps.map((g, i) => (
            <div
              key={i}
              className={`rounded-md border px-2 py-1 text-[11px] ut-stated ut-state-${g.state}`}
            >
              {g.text}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
